import axios from 'axios';
import { toast } from 'sonner';
import { isTossitNativeApp, nativeLogout, getPlatform, getNativeAuthToken } from '../utils/nativeBridge';

const APP_VERSION = process.env.REACT_APP_VERSION || 'dev';

// Central API root — all callers use relative paths (e.g. api.get('/bookings')).
export const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory token fallback. The WebView's localStorage can be unreliable / cleared between
// window.tossitNavigate() deep-link navigations — which would send API calls unauthenticated
// → 401 → spurious logout. AuthContext keeps these in sync (setMemToken) so requests stay
// authenticated across client-side navigation without a reload. Also see the host-token
// fallback + retry-once in the response interceptor below.
const _memTokens = { token: null, cookToken: null, adminToken: null };
export const setMemToken = (key, value) => { if (key in _memTokens) _memTokens[key] = value || null; };
export const clearMemToken = (key) => { if (key in _memTokens) _memTokens[key] = null; };

// Resolve a token: localStorage → in-memory cache → (customer, in-app) native host property.
const readToken = (key) => {
    let t = null;
    try { t = localStorage.getItem(key); } catch (e) { /* ignore */ }
    if (!t && key in _memTokens) t = _memTokens[key];
    if (!t && key === 'token' && isTossitNativeApp()) {
        try { t = (window.TossitNative && window.TossitNative.authToken) || null; } catch (e) { /* ignore */ }
    }
    return t;
};

// True when a logged-in customer token is available (localStorage / in-memory / native host).
export const getCustomerToken = () => readToken('token');

// Pick the right token for the current app section.
// Admin (/admin/*) and cook (/cook/*) have their own tokens; everything else is the customer.
const tokenForPath = () => {
    const p = (typeof window !== 'undefined' && window.location.pathname) || '';
    if (p.startsWith('/admin')) return readToken('adminToken');
    if (p.startsWith('/cook')) return readToken('cookToken');
    return readToken('token');
};

const isCustomerPath = () => {
    const p = (typeof window !== 'undefined' && window.location.pathname) || '';
    return !p.startsWith('/admin') && !p.startsWith('/cook');
};

const isCookPath = () => {
    const p = (typeof window !== 'undefined' && window.location.pathname) || '';
    return p.startsWith('/cook');
};

const api = axios.create({ baseURL: API_ROOT });

// Attach the bearer token automatically (unless the caller set one explicitly).
api.interceptors.request.use((config) => {
    // Client version + platform for the backend stale-client gate.
    config.headers['X-App-Version'] = APP_VERSION;
    config.headers['X-App-Platform'] = getPlatform();
    const existing = config.headers && (config.headers.Authorization || config.headers.authorization);
    if (!existing) {
        const t = tokenForPath();
        if (t) config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
});

// Centralized 401 handling. Customer + cook both hand off to the native host inside
// the WebView (so the web login never shows), or route to their own login on web.
let handling401 = false;
api.interceptors.response.use(
    (res) => res,
    async (error) => {
        const status = error?.response?.status;
        if (status === 426) {
            const detail = error?.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail
                : (isTossitNativeApp() ? 'Please update the app to continue.' : 'Please refresh the app to continue.');
            toast.error(msg, { duration: 12000, id: 'app-outdated' });
            return Promise.reject(error);
        }
        const inactive403 = status === 403 && (isCustomerPath() || isCookPath())
            && typeof error?.response?.data?.detail === 'string'
            && /inactive/i.test(error.response.data.detail);
        if ((status === 401 || inactive403) && (isCustomerPath() || isCookPath())) {
            const cook = isCookPath();
            const key = cook ? 'cookToken' : 'token';

            // In the native app a 401 often just means the host token hasn't been applied yet
            // (e.g. right after a window.tossitNavigate() deep-link). Recover the host token and
            // retry the ORIGINAL request ONCE before tearing down the session. A guard flag on
            // the request config prevents an infinite retry loop, and we only retry when the host
            // actually has a DIFFERENT token than the one that just failed.
            const original = error.config;
            if (status === 401 && isTossitNativeApp() && original && !original._retriedWithHostToken) {
                try {
                    const hostToken = await getNativeAuthToken();
                    const sentAuth = (original.headers && (original.headers.Authorization || original.headers.authorization)) || '';
                    const sentToken = String(sentAuth).replace(/^Bearer\s+/i, '');
                    if (hostToken && hostToken !== sentToken) {
                        original._retriedWithHostToken = true;
                        setMemToken(key, hostToken);
                        try { localStorage.setItem(key, hostToken); } catch (e) { /* ignore */ }
                        original.headers = original.headers || {};
                        original.headers.Authorization = `Bearer ${hostToken}`;
                        return api(original);
                    }
                } catch (e) { /* fall through to logout */ }
            }

            if (!handling401) {
                handling401 = true;
                try {
                    if (cook) {
                        localStorage.removeItem('cookToken');
                        localStorage.removeItem('cookUser');
                        clearMemToken('cookToken');
                    } else {
                        localStorage.removeItem('token');
                        clearMemToken('token');
                    }
                } catch (e) { /* ignore */ }
                toast.error(inactive403 ? error.response.data.detail : 'Your session has expired. Please log in again.');
                if (isTossitNativeApp()) {
                    nativeLogout();
                } else {
                    setTimeout(() => { window.location.assign(cook ? '/cook/login' : '/auth'); }, 900);
                }
                // Allow a future genuine 401 to be handled again (e.g. after re-login in native, no full reload)
                setTimeout(() => { handling401 = false; }, 5000);
            }
        }
        return Promise.reject(error);
    }
);

export default api;
